/**
 * `usePolling` — Story 5.7.
 *
 * Single shared React hook that owns polling-loop semantics across the
 * SPA. Implements architecture §2.7 line 275 verbatim — `usePolling(
 * fetcher, { intervalMs, backoffOnError, staleAfterMs })` returns
 * `{ data, error, isStale, lastUpdatedAt }` — plus the Story 5.7 AC
 * line 1079 ETag extension (consumer-supplied `etag` flows into
 * fetcher; on 304 the cached `data` is treated as fresh and
 * `lastUpdatedAt` advances) and the AC line 1080 cleanup contract
 * (cleanup on unmount AND on `visibilitychange === "hidden"` UNLESS
 * `keepPollingHidden: true`).
 *
 * Forward-pairs (consumers will land in their own stories):
 *   - Story 7.2 (admin dashboard): `{ intervalMs: 4000, backoffOnError:
 *     [4000, 4000, 8000, 16000], staleAfterMs: 30000 }` + Story 5.4
 *     `<StaleBanner />` consumer pattern (UX §3.3.3 lines 525-538).
 *   - Story 8.7 (submitter status page): `{ intervalMs: 10000,
 *     backoffOnError: [10000, 10000, 20000, 30000], staleAfterMs: 60000
 *     }` (UX §4.7 line 1144). Submitter does not see staleness UI.
 *   - Story 9.7 (voter gallery refresh): polls but ignores `isStale`
 *     per UX line 274 — voters do NOT see staleness.
 *   - Story 10.4 (Reveal Control multi-device sync): `{ intervalMs:
 *     1000 }` against `/admin/events/<id>/reveal/state`.
 *   - Story 12.4 (broadcast live board): `{ intervalMs: 5000 }`.
 *   - Story 12.7 (broadcast Reveal Display): `{ intervalMs: 1000,
 *     backoffOnError: [1000, 2000, 4000, 8000], staleAfterMs: Infinity,
 *     keepPollingHidden: true, etag: lastEtag }` (architecture §6.4
 *     §6.6, AR-19).
 *
 * Lifecycle (LOCKED, story spec lines 59-69 + Critical Scope Decisions
 * #3, #4, #5):
 *   1. On mount: schedule the first poll at `setTimeout(..., 0)`
 *      (NOT at `intervalMs`) so consumers see data on first paint.
 *   2. On `kind: 'fresh'`: set `data`, set `lastUpdatedAt`, clear
 *      `error`, reset `failureCount = 0`, schedule next at
 *      `intervalMs`. Internal etag advances if response carries one.
 *   3. On `kind: 'not-modified'`: keep `data`, advance `lastUpdatedAt`,
 *      clear `error`, reset `failureCount = 0`, schedule next at
 *      `intervalMs`. Internal etag stays unchanged (or advances if
 *      response explicitly carries a new one — rare).
 *   4. On rejection / sync throw / unknown `kind`: keep `data` +
 *      `lastUpdatedAt` (last-known-good per architecture §6.6 line
 *      713), set `error`, increment `failureCount`, schedule next at
 *      `backoffOnError[Math.min(failureCount - 1, length - 1)]`.
 *   5. Recovery: any successful resolution after failures resets
 *      `failureCount = 0`; next tick at `intervalMs` (NOT continuing
 *      down the backoff schedule).
 *   6. `visibilitychange === "hidden"` AND `keepPollingHidden !== true`:
 *      abort in-flight fetch, clearTimeout next tick, mark paused.
 *      `data`, `error`, `lastUpdatedAt` persist.
 *   7. `visibilitychange === "visible"` AND paused: schedule immediate
 *      poll (timeout 0), reset `failureCount = 0`.
 *   8. On unmount: abort, clearTimeout, removeEventListener.
 *
 * Single-timer + single-AbortController invariant (CSD #4): the hook
 * owns at most ONE active `setTimeout` AND at most ONE active
 * `AbortController` at any time. The next-tick `setTimeout` is
 * scheduled ONLY AFTER the previous fetcher resolves. Effective polling
 * rate = `max(fetcher_latency, intervalMs)`.
 *
 * `isStale` computation (CSD #2): rendered-time only —
 * `lastUpdatedAt !== null && (Date.now() - lastUpdatedAt) > staleAfterMs`.
 * NOT timer-driven. Updates whenever the hook re-renders (which happens
 * on every poll outcome). Worst-case delay before staleness reflects in
 * UI is one poll interval after the threshold crosses.
 *
 * Stale-closure prevention (CSD #9): all options + the fetcher are
 * captured in refs, updated on every render. The async loop closure
 * reads `<ref>.current` at tick time. Consumers can pass inline option
 * objects without `useMemo`.
 *
 * References:
 *   - Architecture §2.7 line 275 — primary signature.
 *   - Architecture §2.2 line 192 — `hooks/` directory in frontend layout.
 *   - Architecture §6.4 lines 660-672 — broadcast ETag protocol.
 *   - Architecture §6.6 lines 706-716 — broadcast graceful degradation.
 *   - UX §3.3.3 lines 525-538 — admin dashboard StaleBanner.
 *   - UX §3.7 — broadcast common rules.
 *   - UX §4.7 line 1144 — submitter status 10s polling.
 *   - AR-19 (epics.md line 130) — ETag-aware reveal-state polling.
 *   - NFR-NR3 (prd.md line 748) — polling tolerance + auto-resume.
 *   - NFR-P3 (prd.md line 730) — admin dashboard freshness.
 *   - https://react.dev/reference/react/useReducer
 *   - https://react.dev/reference/react/useRef
 *   - https://react.dev/reference/react/useEffect
 *   - https://developer.mozilla.org/en-US/docs/Web/API/setTimeout
 *   - https://developer.mozilla.org/en-US/docs/Web/API/AbortController
 *   - https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
 */
import { useEffect, useReducer, useRef } from 'react';

/**
 * Outcome shape returned by the consumer-supplied fetcher. The
 * discriminated union is the contract: the hook is fetcher-agnostic at
 * the network layer (no HTTP / fetch / status code knowledge), but
 * prescribes the OUTCOME shape so the reducer can transition state
 * deterministically per CSD #7.
 */
export type PollFetchOutcome<TData> =
  | { kind: 'fresh'; data: TData; etag?: string | null }
  | { kind: 'not-modified'; etag?: string | null };

/**
 * Consumer-supplied fetcher. Receives the in-flight fetch's
 * AbortSignal (must be honored — pass to `fetch(url, { signal })` or
 * check `signal.aborted` before resolving) and the current ETag (the
 * value the consumer originally passed via `options.etag`, advanced as
 * the hook learns new etags from `kind: 'fresh'` outcomes).
 */
export type PollFetcher<TData> = (
  signal: AbortSignal,
  etag: string | null | undefined,
) => Promise<PollFetchOutcome<TData>>;

export interface UsePollingOptions<TData> {
  /** Base poll interval in milliseconds (success path). */
  intervalMs: number;
  /**
   * Backoff schedule for consecutive failures: failure N →
   * `backoffOnError[Math.min(N, length - 1)]`. Capped at the LAST
   * element. E.g., `[4000, 4000, 8000, 16000]` — failures 4+ all use
   * 16_000.
   */
  backoffOnError: number[];
  /**
   * `isStale = lastUpdatedAt !== null && (now - lastUpdatedAt) >
   * staleAfterMs`. Pass `Infinity` to disable.
   */
  staleAfterMs: number;
  /**
   * Optional initial ETag to send as `If-None-Match` on the first poll
   * (AR-19). The hook tracks the etag from successful responses and
   * forwards the latest known value to subsequent fetcher calls. Pass
   * `undefined` (default) to opt out of ETag-aware polling — the
   * fetcher's `etag` arg will be `undefined` and the fetcher should
   * never return `kind: 'not-modified'`.
   */
  etag?: string | null;
  /**
   * Continue polling when `document.visibilityState === 'hidden'`.
   * Default `false`. Set `true` for broadcast surfaces (architecture
   * §6.6 line 716 — OBS browser sources are 'hidden' but visible to
   * operator).
   */
  keepPollingHidden?: boolean;
}

export interface UsePollingResult<TData> {
  /** Last successful response body, or `null` until first success. */
  data: TData | null;
  /**
   * Most recent error, or `null` if last poll succeeded. Cleared on
   * next success.
   */
  error: Error | null;
  /**
   * `true` iff `(now - lastUpdatedAt) > staleAfterMs`. `false` until
   * first success.
   */
  isStale: boolean;
  /**
   * Wall-clock timestamp (ms since epoch) of last successful response
   * (200 OR 304). `null` before first success.
   */
  lastUpdatedAt: number | null;
}

/**
 * Reducer state. `failureCount` is in state (not a ref) because the
 * reducer's transitions need consistent reads of it; transitions are
 * also more testable as pure functions on this shape.
 */
interface State<TData> {
  data: TData | null;
  error: Error | null;
  lastUpdatedAt: number | null;
  failureCount: number;
}

type Action<TData> =
  | { type: 'fresh'; data: TData; at: number }
  | { type: 'not-modified'; at: number }
  | { type: 'error'; error: Error }
  | { type: 'reset-on-resume' };

function reducer<TData>(state: State<TData>, action: Action<TData>): State<TData> {
  switch (action.type) {
    case 'fresh':
      return {
        data: action.data,
        error: null,
        lastUpdatedAt: action.at,
        failureCount: 0,
      };
    case 'not-modified':
      return {
        ...state,
        error: null,
        lastUpdatedAt: action.at,
        failureCount: 0,
      };
    case 'error':
      return {
        ...state,
        error: action.error,
        failureCount: state.failureCount + 1,
      };
    case 'reset-on-resume':
      return {
        ...state,
        failureCount: 0,
      };
    default:
      return state;
  }
}

const INITIAL_STATE: State<unknown> = {
  data: null,
  error: null,
  lastUpdatedAt: null,
  failureCount: 0,
};

/**
 * Coerces a thrown value into an Error instance per Lifecycle item 4.
 * React's reducer state contract for `error` is `Error | null`; the
 * fetcher might reject with a non-Error (string, object, etc.), so we
 * wrap defensively.
 */
function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  return new Error(String(value));
}

export function usePolling<TData>(
  fetcher: PollFetcher<TData>,
  options: UsePollingOptions<TData>,
): UsePollingResult<TData> {
  const [state, dispatch] = useReducer(
    reducer as React.Reducer<State<TData>, Action<TData>>,
    INITIAL_STATE as State<TData>,
  );

  // Refs for transient mutable values (CSD #9). Setting refs on every
  // render keeps the loop closure reading the latest values without
  // forcing consumer-side memoization.
  const fetcherRef = useRef<PollFetcher<TData>>(fetcher);
  const intervalMsRef = useRef<number>(options.intervalMs);
  const backoffOnErrorRef = useRef<number[]>(options.backoffOnError);
  const keepPollingHiddenRef = useRef<boolean>(options.keepPollingHidden === true);

  // The hook tracks the live etag separately from `options.etag`. The
  // option's value is the INITIAL etag; subsequent successful responses
  // advance the internal etag. When the consumer EXPLICITLY changes
  // `options.etag` between renders (e.g., a fresh snapshot fetch outside
  // the hook), the new value supersedes the hook-learned one.
  // `lastSeenOptionEtagRef` tracks the most recent option value the hook
  // observed, so that re-renders with the same option value do NOT
  // clobber a hook-learned etag.
  const etagRef = useRef<string | null | undefined>(options.etag);
  const lastSeenOptionEtagRef = useRef<string | null | undefined>(options.etag);

  // Update refs on every render so the loop closure sees the latest
  // values at tick time.
  fetcherRef.current = fetcher;
  intervalMsRef.current = options.intervalMs;
  backoffOnErrorRef.current = options.backoffOnError;
  keepPollingHiddenRef.current = options.keepPollingHidden === true;

  // Failure-count ref mirrors the reducer state for read-access inside
  // the loop closure (the closure cannot read `state.failureCount`
  // without re-creating itself; mirroring through a ref avoids that).
  const failureCountRef = useRef<number>(0);
  failureCountRef.current = state.failureCount;

  // Refs that drive the lifecycle invariants (CSD #4). One timer, one
  // AbortController, one mounted flag, one paused flag — all at most.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(false);
  const pausedRef = useRef<boolean>(false);

  // Reflect `options.etag` (the consumer-supplied value) into the
  // internal ref ONLY when the consumer's option value has actually
  // changed since the last render. This preserves the contract that
  // hook-learned etags (from `kind: 'fresh'` responses) stay live
  // across re-renders that pass the same `options.etag`. A consumer
  // who explicitly swaps `options.etag` (e.g., re-seeds from an out-of-
  // band snapshot fetch) gets their value applied; a consumer who
  // simply re-renders with the same prop does not clobber the hook's
  // advanced etag.
  if (options.etag !== lastSeenOptionEtagRef.current) {
    lastSeenOptionEtagRef.current = options.etag;
    if (options.etag !== undefined) {
      etagRef.current = options.etag;
    }
  }

  useEffect(() => {
    mountedRef.current = true;

    const clearActiveTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const abortInflight = () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };

    /**
     * Schedules the next poll. `delay` is the milliseconds to wait
     * before the NEXT tick fires (which itself runs `tick()` again).
     * Lock: `clearActiveTimer()` first to preserve the single-timer
     * invariant — a defensive no-op when no timer is pending.
     */
    const scheduleNext = (delay: number) => {
      if (!mountedRef.current || pausedRef.current) return;
      clearActiveTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        // Re-check mount + pause inside the timer firing; visibility
        // could have flipped between scheduling and firing.
        if (!mountedRef.current || pausedRef.current) return;
        void tick();
      }, delay);
    };

    /**
     * One iteration of the polling loop. Runs the fetcher, dispatches
     * the resulting action, and schedules the next tick. Lock: this
     * function is the SOLE caller of `fetcherRef.current(...)` and the
     * SOLE place a new AbortController is created.
     */
    const tick = async () => {
      if (!mountedRef.current || pausedRef.current) return;

      // Fresh AbortController per cycle (CSD #4 idempotency lock).
      const controller = new AbortController();
      abortRef.current = controller;

      let outcome: PollFetchOutcome<TData> | null = null;
      let thrown: unknown = null;
      try {
        outcome = await fetcherRef.current(controller.signal, etagRef.current);
      } catch (err) {
        thrown = err;
      }

      // If the cycle's controller has been replaced (cleanup ran, or a
      // visibility-resume scheduled a fresh tick mid-flight), drop this
      // outcome. This prevents stale resolves from clobbering newer
      // state. Belt + suspenders alongside the AbortController abort.
      if (abortRef.current !== controller) return;
      abortRef.current = null;

      // If unmounted while awaiting, do nothing. The cleanup function
      // already cleared timers + aborted; we just exit before
      // dispatching.
      if (!mountedRef.current) return;

      // If the abort signal fired (visibility-pause OR unmount race),
      // do not dispatch — the resume / unmount handlers own state from
      // here.
      if (controller.signal.aborted) return;

      const at = Date.now();
      if (thrown !== null) {
        dispatch({ type: 'error', error: toError(thrown) });
        // Read the JUST-incremented failure count from the ref. The
        // dispatch above is async w.r.t. the ref; we read the ref's
        // pre-dispatch value and add 1 to mirror the reducer's
        // `failureCount + 1`.
        const nextFailure = failureCountRef.current + 1;
        const schedule = backoffOnErrorRef.current;
        const delay =
          schedule.length === 0
            ? intervalMsRef.current
            : schedule[Math.min(nextFailure - 1, schedule.length - 1)] ??
              intervalMsRef.current;
        scheduleNext(delay);
        return;
      }

      // Outcome must be one of the union members. Defensive: an unknown
      // shape is treated as a failure.
      if (outcome === null || (outcome.kind !== 'fresh' && outcome.kind !== 'not-modified')) {
        const err = new Error(
          `usePolling: fetcher resolved with unexpected outcome shape (expected { kind: 'fresh' | 'not-modified' }).`,
        );
        dispatch({ type: 'error', error: err });
        const nextFailure = failureCountRef.current + 1;
        const schedule = backoffOnErrorRef.current;
        const delay =
          schedule.length === 0
            ? intervalMsRef.current
            : schedule[Math.min(nextFailure - 1, schedule.length - 1)] ??
              intervalMsRef.current;
        scheduleNext(delay);
        return;
      }

      if (outcome.kind === 'fresh') {
        // Advance internal etag if the fetcher returned one. Lock:
        // `etag === undefined` on the outcome means "etag unchanged"
        // (preserve the prior internal value). `etag === null` is
        // distinct — a server clearing the etag — and is honored.
        if (outcome.etag !== undefined) {
          etagRef.current = outcome.etag;
        }
        dispatch({ type: 'fresh', data: outcome.data, at });
      } else {
        // 304-equivalent. `data` stays at last-known-good (handled in
        // the reducer). Etag may advance if the server reissued one.
        if (outcome.etag !== undefined) {
          etagRef.current = outcome.etag;
        }
        dispatch({ type: 'not-modified', at });
      }
      scheduleNext(intervalMsRef.current);
    };

    /**
     * Visibility handler. Defaults to pause-on-hidden / resume-on-
     * visible. Opt-out via `keepPollingHidden: true` (broadcast
     * surfaces) per CSD #5.
     */
    const handleVisibilityChange = () => {
      if (keepPollingHiddenRef.current) return;
      if (typeof document === 'undefined') return;

      if (document.visibilityState === 'hidden') {
        pausedRef.current = true;
        clearActiveTimer();
        abortInflight();
      } else if (document.visibilityState === 'visible' && pausedRef.current) {
        pausedRef.current = false;
        // Reset failure index on resume per Lifecycle item 7. Architecture
        // §6.4 doesn't mandate this, but UX §3.3.3 line 526 ("Auto-
        // dismisses when polling resumes") implies a fresh start.
        // Reset BOTH the reducer state (so render reflects it) AND the
        // ref (so the next `tick` reads 0 even if the timer fires before
        // React commits the dispatch). Without the ref-side reset, a
        // resumed fetch that fails would compute backoff using the
        // pre-pause failure count under any scheduler that fires the
        // timer ahead of React's render commit.
        failureCountRef.current = 0;
        dispatch({ type: 'reset-on-resume' });
        // Schedule the resume burst at timeout 0.
        scheduleNext(0);
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // First poll fires immediately (timeout 0) per CSD #3. If the page
    // was already hidden when the hook mounted AND `keepPollingHidden`
    // is false, do NOT schedule — wait for visibility to flip.
    const startHidden =
      !keepPollingHiddenRef.current &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden';
    if (startHidden) {
      pausedRef.current = true;
    } else {
      scheduleNext(0);
    }

    return () => {
      mountedRef.current = false;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      clearActiveTimer();
      abortInflight();
      pausedRef.current = false;
    };
    // Empty dep array: the loop captures refs (CSD #9). Re-running the
    // effect on every fetcher / option change would tear down + restart
    // the polling loop on every render — the EXACT opposite of polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `isStale` is computed at render time (CSD #2). Updates every time
  // the hook re-renders (which happens on every poll outcome via
  // `dispatch`). Between polls, the value is "frozen" at its last
  // rendered value; consumers re-render at the polling cadence so
  // worst-case staleness reflects within one interval after the
  // threshold crosses.
  const isStale =
    state.lastUpdatedAt !== null &&
    Date.now() - state.lastUpdatedAt > options.staleAfterMs;

  return {
    data: state.data,
    error: state.error,
    isStale,
    lastUpdatedAt: state.lastUpdatedAt,
  };
}
